// =============================================================================
// Pricing & Credits config service. Talks to the .NET SaaS Tracker API.
// Lets the admin portal edit: the action price list (ActionPointMasters, what
// the usage queue charges from), the top-up bundles (credits + price), and the
// trial/tax policy.
// =============================================================================
import { getCurrentPrincipal, getIdToken } from '../../../services/cognitoAuth';

const API_ROOT =
  (process.env.REACT_APP_SAAS_TRACKER_URL as string | undefined) ?? 'http://localhost:5080/api';

async function authHeaders(): Promise<Record<string, string>> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  try { const t = await getIdToken(); if (t) h.Authorization = `Bearer ${t}`; } catch { /* dev: no auth */ }
  return h;
}

async function req(path: string, method: string, body?: any): Promise<any> {
  const res = await fetch(`${API_ROOT}${path}`, {
    method,
    headers: await authHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) throw new Error(`${method} ${path} failed (${res.status})`);
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}

/**
 * DEPRECATED: the old price book. Credits are no longer charged from it; the usage
 * queue charges from ActionPointMasters (see ActionPoint below). Kept only so older
 * code compiles; the Pricing page no longer uses it.
 */
export interface PriceBookEntry {
  id: string; actionKey: string; module: string; displayName: string; creditCost: number; active: boolean;
}
export interface TopUpBundle {
  id: string; credits: number; priceUsd: number; popular: boolean; discountPct?: number | null; sortOrder: number; active: boolean;
}
export interface TrialPolicy {
  id: number; readOnlyTrialDays: number; dataTrialDays: number; freeCredit: number;
  promptAfterMinutes: number; currency: string; thresholdsCsv: string; taxPercent: number;
}

export const getPriceBook = () => req('/config/pricebook', 'GET') as Promise<PriceBookEntry[]>;
export const savePriceBook = (e: Omit<PriceBookEntry, 'id'>) => req('/config/pricebook', 'PUT', e);

export const getBundles = () => req('/config/topup-bundles', 'GET') as Promise<TopUpBundle[]>;
export const saveBundle = (b: Partial<TopUpBundle>) => req('/config/topup-bundles', 'PUT', b);

export const getTrialPolicy = () => req('/config/trial-policy', 'GET') as Promise<TrialPolicy>;
export const saveTrialPolicy = (p: TrialPolicy) => req('/config/trial-policy', 'PUT', p);

// ---- Action price list (ActionPointMasters) ----------------------------------
// actionName is what the Logipulse services send with each action (usually the last
// segment of the route, e.g. CreateShipment). points = credits deducted per action.
// Only one ACTIVE row may exist per actionName.

export interface ActionPoint {
  actionPointMasterId: number;
  actionPointMasterGuid: string;
  moduleName: string | null;
  actionName: string;
  functionName: string | null;
  points: number;
  isActive: boolean;
  createdDateUtc: string;
  modifiedDateUtc: string | null;
  createdBy: string | null;
  modifiedBy: string | null;
}

export interface ActionPointInput {
  actionName: string;
  points: number;
  moduleName: string | null;
  functionName: string | null;
  isActive: boolean;
}

/** Like req(), but a 400 from the API shows its own message (e.g. a duplicate active action). */
async function reqWithMessage(path: string, method: string, body?: any): Promise<any> {
  const res = await fetch(`${API_ROOT}${path}`, {
    method,
    headers: await authHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  let json: any = null;
  try { json = text ? JSON.parse(text) : null; } catch { /* not JSON */ }
  if (!res.ok) throw new Error(json?.error || `${method} ${path} failed (${res.status})`);
  return json;
}

/** Who made the change, recorded as CreatedBy / ModifiedBy. */
async function changedBy(): Promise<string> {
  try {
    const p = await getCurrentPrincipal();
    return p?.email || p?.username || 'backoffice';
  } catch { return 'backoffice'; }
}

/** Every action in the price list (all pages). */
export async function getActionPoints(): Promise<ActionPoint[]> {
  const all: ActionPoint[] = [];
  for (let page = 1; page <= 50; page++) {
    const r = await reqWithMessage(`/admin/action-point-masters?page=${page}&pageSize=200`, 'GET');
    const items: ActionPoint[] = r?.items ?? [];
    all.push(...items);
    if (all.length >= (r?.total ?? 0) || items.length === 0) break;
  }
  return all;
}

export const createActionPoint = async (a: ActionPointInput): Promise<ActionPoint> =>
  reqWithMessage('/admin/action-point-masters', 'POST', { ...a, changedBy: await changedBy() });

export const updateActionPoint = async (guid: string, a: ActionPointInput): Promise<ActionPoint> =>
  reqWithMessage(`/admin/action-point-masters/${guid}`, 'PUT', { ...a, changedBy: await changedBy() });

/**
 * Reloads the API's price cache at once. The SqsConsumer Lambda keeps its own copy and
 * picks up a change within its refresh time (UsageCache:ActionPointRefreshMinutes, 10 by default).
 */
export const clearUsageCache = () =>
  reqWithMessage('/admin/usage-cache/clear', 'POST') as Promise<{ cleared: boolean; actionPoints: number }>;
